import React, { useMemo, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { ro } from "date-fns/locale";
import Select from "react-select";
import { NumericFormat } from "react-number-format";
import "react-datepicker/dist/react-datepicker.css";
import "./Add_Edit_ExchangeRate.css";
import axiosInstance from "../../../Config/axiosInstance";

registerLocale("ro", ro);

const SYSTEM_CURRENCY = "EUR";

// Stiluri pentru meniul react-select astfel încât dropdown-ul să apară peste modal.
const selectMenuStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 20000 }),
  menu: (base) => ({ ...base, zIndex: 20000 }),
};

const AddExchangeRate = ({ onClose, onSuccess, currencies }) => {
  // Datele formularului.
  const [formData, setFormData] = useState({
    currency: "",
    value: "",
    date: new Date(),
  });

  // Erorile de validare pe câmpuri.
  const [fieldErrors, setFieldErrors] = useState({
    currency: "",
    value: "",
    date: "",
  });

  // Starea de loading la submit.
  const [loading, setLoading] = useState(false);

  // Construiește opțiunile pentru dropdown-ul de monede.
  const currencyOptions = useMemo(
    () =>
      currencies.map((currency) => ({
        value: currency.id,
        label: `${currency.name} (${currency.abbreviation})`,
        abbreviation: currency.abbreviation,
        name: currency.name,
      })),
    [currencies]
  );

  // Găsește opțiunea selectată pe baza valorii din state.
  const selectedCurrencyOption = useMemo(
    () =>
      currencyOptions.find(
        (option) => String(option.value) === String(formData.currency)
      ) || null,
    [currencyOptions, formData.currency]
  );

  // Prescurtarea monedei selectate, folosită în textele explicative.
  const selectedCurrencyAbbreviation =
    selectedCurrencyOption?.abbreviation || selectedCurrencyOption?.label || "";

  // Formatează valoarea introdusă pentru previzualizare.
  const formattedPreviewValue = useMemo(() => {
    if (
      formData.value === null ||
      formData.value === undefined ||
      formData.value === ""
    ) {
      return "";
    }

    const numValue = Number(formData.value);
    if (Number.isNaN(numValue)) return "";

    return new Intl.NumberFormat("ro-RO", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 6,
    }).format(numValue);
  }, [formData.value]);

  // Actualizează data selectată și șterge eventuala eroare.
  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      date,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      date: "",
    }));
  };

  // Actualizează moneda selectată și șterge eventuala eroare.
  const handleCurrencyChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      currency: selectedOption ? selectedOption.value : "",
    }));

    setFieldErrors((prev) => ({
      ...prev,
      currency: "",
    }));
  };

  // Actualizează valoarea cursului și șterge eventuala eroare.
  const handleValueChange = ({ floatValue }) => {
    setFormData((prev) => ({
      ...prev,
      value: floatValue !== undefined ? floatValue : "",
    }));

    setFieldErrors((prev) => ({
      ...prev,
      value: "",
    }));
  };

  // Limitează valoarea maximă permisă în input.
  const isAllowed = ({ floatValue }) => {
    return floatValue === undefined || floatValue <= 99999999999;
  };

  // Validează câmpurile formularului înainte de submit.
  const validateForm = () => {
    const errors = {};

    if (!formData.currency) {
      errors.currency = "Currency is required";
    }

    if (formData.value === "" || formData.value === null) {
      errors.value = `Conversion rate to ${SYSTEM_CURRENCY} is required`;
    } else if (Number.isNaN(Number(formData.value)) || Number(formData.value) <= 0) {
      errors.value = "Exchange rate must be a positive number";
    } else if (Number(formData.value) > 99999999999) {
      errors.value = "Exchange rate cannot exceed 99,999,999,999.9999";
    }

    if (!formData.date) {
      errors.date = "Date is required";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Trimite formularul către backend după validare.
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    const isValid = validateForm();
    if (!isValid) return;

    setLoading(true);

    const newExchangeRate = {
      currency: parseInt(formData.currency, 10),
      value: parseFloat(formData.value),
      date: formData.date.toISOString().split("T")[0],
    };

    try {
      const response = await axiosInstance.post("/exchange-rate/", newExchangeRate);

      if (response.status === 201) {
        onSuccess(response.data);
        onClose();
      }
    } catch (error) {
      if (error.response?.data) {
        const backendErrors = {};

        Object.keys(error.response.data).forEach((key) => {
          backendErrors[key] = Array.isArray(error.response.data[key])
            ? error.response.data[key][0]
            : error.response.data[key];
        });

        setFieldErrors((prev) => ({
          ...prev,
          ...backendErrors,
        }));
      } else {
        alert(`An error occurred: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-exchangerate">
      <div className="add-exchangerate-page">
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Add Exchange Rate</h2>
              <button
                type="button"
                className="close-btn"
                onClick={onClose}
                disabled={loading}
              >
                ×
              </button>
            </div>

            {loading && (
              <div className="loading-overlay">
                <div className="loader"></div>
                <span>Adding exchange rate...</span>
              </div>
            )}

            <form className="form" onSubmit={handleSubmit}>
              <div className="form-field">
                <label className="label-left">
                  Currency <span className="required">*</span>
                </label>

                <Select
                  name="currency"
                  options={currencyOptions}
                  value={selectedCurrencyOption}
                  onChange={handleCurrencyChange}
                  placeholder="Select Currency"
                  className={`multiselect-field ${fieldErrors.currency ? "select-error" : ""
                    }`}
                  classNamePrefix="select"
                  isSearchable
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectMenuStyles}
                />

                {fieldErrors.currency && (
                  <div className="field-error">{fieldErrors.currency}</div>
                )}
              </div>

              <div className="form-field">
                <label className="label-left">
                  Conversion to System Currency ({SYSTEM_CURRENCY}){" "}
                  <span className="required">*</span>
                </label>

                <div className="currency-wrapper">
                  <NumericFormat
                    value={formData.value}
                    onValueChange={handleValueChange}
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={4}
                    fixedDecimalScale={false}
                    allowNegative={false}
                    inputMode="decimal"
                    placeholder="0,0000"
                    className={`numeric-input ${fieldErrors.value ? "field-error-border" : ""
                      }`}
                    isAllowed={isAllowed}
                  />
                </div>

                {selectedCurrencyAbbreviation && (
                  <div className="exchange-rate-helper-text">
                    {`Enter how many ${SYSTEM_CURRENCY} equals 1 ${selectedCurrencyAbbreviation}`}
                  </div>
                )}

                {selectedCurrencyAbbreviation && formattedPreviewValue && (
                  <div className="exchange-rate-preview-text">
                    {`1 ${selectedCurrencyAbbreviation} = ${formattedPreviewValue} ${SYSTEM_CURRENCY}`}
                  </div>
                )}

                {fieldErrors.value && (
                  <div className="field-error">{fieldErrors.value}</div>
                )}
              </div>

              <div className="form-field">
                <label className="label-left">
                  Date <span className="required">*</span>
                </label>

                <div className="datepicker-container">
                  <DatePicker
                    selected={formData.date}
                    onChange={handleDateChange}
                    dateFormat="dd.MM.yyyy"
                    locale="ro"
                    placeholderText="dd.mm.yyyy"
                    className={`datepicker-input-with-icon ${fieldErrors.date ? "field-error-border" : ""
                      }`}
                    popperPlacement="bottom-start"
                    popperContainer={({ children }) => (
                      <div style={{ position: "fixed", zIndex: 20001 }}>
                        {children}
                      </div>
                    )}
                  />

                  <span className="calendar-icon-inside">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </span>
                </div>

                {fieldErrors.date && (
                  <div className="field-error">{fieldErrors.date}</div>
                )}
              </div>

              <div className="form-buttons">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className={`submit-btn ${loading ? "disabled" : ""}`}
                  disabled={loading}
                >
                  {loading ? "Adding..." : "Add Exchange Rate"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddExchangeRate;